import Handlebars from "../handlebars/handlebars";
import errorResponseHandler from "../errors/errorResponseHandler";
import Modal from "../modal/Modal";
import "../print/printService";
import CartRowController from "./CartRowController";
import SavedRowController from "./SavedRowController";
import ShippingEstimateController from "../shipping/ShippingEstimateController";
import shippingEstimateTitleHtml from "../shipping/shippingEstimateTitle.hbs";
import shippingDiscountDisclaimerTitleHtml from "../shipping/shippingDiscountDisclaimerTitle.hbs";
import shippingDiscountDisclaimerBodyHtml from "../shipping/shippingDiscountDisclaimerBody.hbs";
import cartHtml from "./cart.hbs"
import cartEmptyHtml from "./cartEmpty.hbs";
import companionFileSpinnerHtml from "./companionFileSpinner.hbs";
import savedForLaterRowHtml from "./savedForLaterRow.hbs";
import fileUploadWarningsHtml from "./fileUploadWarnings.hbs";
import loyaltyPointsService from "../_services/loyaltyPointsService";
import pendingCompanionFileService from "../_services/pendingCompanionFileService";

const cartContainer = document.getElementById('spdcCart');

const state = {
    loggedIn: false,
    cart: {},
    saved: {},
    savedItems: [],
    promoErrors: {},
    pointsAvailable: 0,
    requestProcessing: false,
    loyaltyCustomer: null,
}

const CSS = {
    MODAL_IS_ACTIVE: 'modal_isActive',
    DIALOG_BODY_LOCK: 'bodyIsLocked_dialog',
    IS_DISABLED: 'isDisabled',
    INPUT_ERRORS: 'spdcForm-row-item_hasErrors'
}

Promise.allSettled([
    Collaterate.User.getUser(),
    Collaterate.Project.getCart(),
    Collaterate.Project.getSaved(),
])
    .then(([user, cart, saved]) => {
        state.loggedIn = Boolean(user.value);
        updateLocalSaved(saved.value);
        return updateLocalCart(cart.value);
    })
    .then(initCartView)
    .then(renderCart)
    .then(pushViewCartEvent)
    .then(attachPendingCompanionDataFiles)

function initCartView () {
    cartContainer.addEventListener('click', handleCartClicks, false);
    cartContainer.addEventListener('ProjectUpdated', handleProjectUpdated);
    cartContainer.addEventListener('ItemRemoved', handleItemRemoved);
    cartContainer.addEventListener('ItemSavedForLater', handleItemSavedForLater);
    cartContainer.addEventListener('ItemMovedToCart', handleItemMovedToCart);
    cartContainer.addEventListener('FileStatusUpdated', renderFileUploadWarnings);
    window.addEventListener('loyaltyPointsRedeemed', updateCart);
    window.addEventListener('loyaltyPointsCancelled', updateCart);
}

function updateCart () {
    enableLoadingState();
    Collaterate.Project.getCart()
        .then( cart => {
            updateLocalCart(cart)
        })
        .finally(disableLoadingState)
}

function getProjectFromOrderItem (orderItem) {
    if (state.saved.id && state.saved.id === orderItem.projectId) {
        return state.saved;
    }

    return state.cart;
}

////////////////////////////
//Event Handlers
////////////////////////////

function handleProjectUpdated (e) {
    const project = e.detail;

    if (!project) {
        return;
    }

    // Both updates are SYNCHRONOUS -- updateLocalCart/updateLocalSaved return the enriched project,
    // not a promise -- so the render is called once, after the branch, rather than chained off a
    // return value. `updateLocalCart(project).then(renderCart)` threw
    // "TypeError: ...then is not a function", which aborted this handler before any render: an item
    // removed from the cart stayed on screen until a full page reload. The load path never showed it
    // because returning a plain object from inside a .then() chain is auto-wrapped.
    if (project.isCart) {
        updateLocalCart(project);
    } else {
        updateLocalSaved(project);
    }

    renderCart();
}

function handleItemRemoved (e) {
    const item  = e.detail;

    if (!item) {
        return;
    }

    pushRemoveFromCartEvent(item);
}

function handleItemSavedForLater (e) {
    const { item, loggedIn } = e.detail;

    if (!item) {
        return;
    }

    pushSaveForLaterEvent(item);
    Collaterate.Project.getCart()
        .then(cart => {
            state.cart = cart;
            return state.cart.getItemById(item.id)
        })
        .then(cartItem => Collaterate.Project.moveItemToSaved(cartItem))
        .then(updateLocalCart)
        .then(Collaterate.Project.getSaved)
        .then(updateLocalSaved)
        .catch(error => {
            item.pending = false;
            errorResponseHandler.generateErrorHandler()(error)
        })
        .finally(() => {
            loggedIn !== state.loggedIn
                ? window.location.reload()
                : renderCart();
        });
}

// Recipient data files chosen on the product page are held in IndexedDB (no order item exists
// yet at that point). On arrival in the cart, attach them to the newest auto-added companion
// envelope item — they become that job's print files — then drain the store.
// A source item can auto-add several companions; the envelope print-ready file goes to the
// ENVELOPE companion. Prefer one whose name reads "envelope"; otherwise fall back to the
// most recently added companion so the file still lands somewhere sensible.
function pickEnvelopeCompanion (companions) {
    if (!companions || !companions.length) {
        return null;
    }
    const newest = (a, b) => (a.id > b.id ? a : b);
    const envelopes = companions.filter(item => /envelope/i.test(item.name || ''));
    return (envelopes.length ? envelopes : companions).reduce(newest);
}

function attachPendingCompanionDataFiles () {
    const pending = pendingCompanionFileService.list();
    if (!pending.length || !state.cart || !state.cart.id) {
        return;
    }

    // A source item can auto-add several companions (e.g. a brochure AND envelopes); the
    // envelope print-ready file belongs to the ENVELOPE one, so target it specifically
    // rather than "whichever was added last". updateLocalCart has already stamped
    // companionSourceId onto the SDK items, so we can find it without a fetch.
    const localCompanions = state.cart.items.filter(item => item.companionSourceId);
    const targetCompanion = pickEnvelopeCompanion(localCompanions);
    if (!targetCompanion) {
        return; // no companion to receive the file (yet) — the staged files keep until they expire
    }
    const sdkItem = state.cart.getItemById(targetCompanion.id);
    if (!sdkItem) {
        return; // not in the SDK model yet — keep the files for the next cart load
    }
    showCompanionFileSpinner(targetCompanion.id);

    // The bytes went up on the product page, so this only binds each staged file to the job.
    // Drop each record as its own attach lands, so one failure keeps that file pending for the
    // next cart load instead of discarding the customer's upload silently.
    Promise.all(pending.map(record => Collaterate.Assets.attachStagedFile(sdkItem, record.reservationName)
        .then(() => pendingCompanionFileService.remove(record.reservationName))
        .catch(() => { /* non-fatal: customer can still upload from the files modal */ })))
        .then(() => Collaterate.Project.getCart())
        .then(updateLocalCart)
        .then(renderCart) // rebuilds the cell, replacing the spinner with the file
        .catch(() => {});
}

// Replaces the companion's file cell with a spinner while its pending upload runs; renderCart
// rebuilds the cell (showing the attached file) once the upload completes. Markup comes from
// companionFileSpinner.hbs and its styling from cart.css, so nothing is assembled as a string here
// and no <style> block is injected at runtime.
function showCompanionFileSpinner (companionId) {
    const cell = document.querySelector(`#cart-item-${companionId} .orderItem-files`);

    if (!cell) {
        return;
    }

    cell.innerHTML = companionFileSpinnerHtml();
}

function handleItemMovedToCart (e) {
    const item = e.detail;

    if (!item) {
        return;
    }

    Collaterate.Project.moveItemToCart(item)
        .then(updateLocalSaved)
        .then(Collaterate.Project.getCart)
        .then(updateLocalCart)
        .then(() => pushMoveToCartEvent(item))
        .catch(error => {
            item.pending = false;
            errorResponseHandler.generateErrorHandler()(error)
        })
        .finally(renderCart);
}

function handleCartClicks (e) {
    // Apply Promo Code
    if (e.target.matches('.js-promoInputSubmit')) {
        handlePromoInputSubmit(e)
        return;
    }

    // Remove Promo Code
    if (e.target.matches('.js-promoInputRemove')) {
        handlePromoInputRemove(e)
        return;
    }

    // Shipping Estimate
    if (e.target.matches('.js-shippingEstimate')) {
        e.preventDefault();
        setShippingEstimate();
        return;
    }

    // Shipping Disclaimer
    if (e.target.matches('.js--element-shipDisclaimer')) {
        e.preventDefault();
        handleShipDisclaimerDisplay();
        return;
    }

    // Checkout Button
    if (e.target.matches('.js-checkoutBtn')) {
        pushBeginCheckoutEvent();
    }

}

////////////////////////////
// SHIPPING ESTIMATION
////////////////////////////

function setShippingEstimate () {
    const modal = new Modal({
        title: shippingEstimateTitleHtml(),
        classes: ['globalDialog_wide'],
        controller: ShippingEstimateController,
        controllerInject: [state.cart, state.loggedIn, true, false],
    });

    return modal.promise
        .then(updateLocalCart)
        .then(renderCart);
}

////////////////////////////
// SHIPPING DISCOUNT DISCLAIMER
////////////////////////////

function handleShipDisclaimerDisplay () {
    const modal = new Modal({
        title: shippingDiscountDisclaimerTitleHtml(),
        content: shippingDiscountDisclaimerBodyHtml(),
    });
}

///////////////////////////////////
// PROMO CODE
//////////////////////////////////

function handlePromoInputSubmit (e) {
    e.preventDefault();
    const promoCode = document.getElementById('js-promoCodeInput').value;

    enableLoadingState();
    Collaterate.Project.setPromoCode(state.cart, promoCode)
        .then(updateLocalCart)
        .then(setValid)
        .catch(errorResponseHandler.generateErrorHandler({
            list: e => {
                setValid();
                e.errors.forEach(error => state.promoErrors[error.field] = error.message);
            },
        }))
        .finally(disableLoadingState);
}

function enableLoadingState () {
    state.requestProcessing = true;
    renderCart();
}

function disableLoadingState () {
    state.requestProcessing = false;
    renderCart();
}

function setValid () {
    state.promoErrors = {};
}

function handlePromoInputRemove (e) {
    e.preventDefault();

    enableLoadingState();
    Collaterate.Project.removePromoCode(state.cart)
        .then(updateLocalCart)
        .finally(disableLoadingState)
}

function updateLocalCart (cartResponse) {
    state.cart = cartResponse;

    if (state.cart.loyaltyDiscount !== null) {
        if ((state.cart.items.length === 0) || (state.cart.subtotalPrice < state.cart.loyaltyDiscount.amount)) {
            loyaltyPointsService.remove();
            return nameCompanionSources(state.cart);
        }
    }

    loyaltyPointsService.udpateMaxApply(state.cart.subtotalPrice);
    return nameCompanionSources(state.cart);
}

// Companion linkage comes from the SDK per item (companionSourceId, companionItemNames); the only
// thing it cannot know is the SOURCE ITEM'S NAME, which is a lookup across the whole project.
// Plain assignment is enough: OrderItem instances are extensible and the SDK does not model this
// field, so there is no read-only property to work around.
function nameCompanionSources (project) {
    const items = (project && project.items) || [];
    const nameById = new Map(items.map(item => [item.id, item.name]));
    items.forEach(item => {
        item.companionSourceName = item.companionSourceId ? nameById.get(item.companionSourceId) : null;
    });
    return project;
}

// Saved-for-later rows get the same companion treatment as the cart: the linkage is enriched and
// each companion is ordered directly under its source so the connector styling (which keys off
// adjacent .orderItem_isCompanion rows) lines up. Ordering is kept in state.savedItems rather than
// written back onto the SDK project.
function updateLocalSaved (savedProject) {
    state.saved = nameCompanionSources(savedProject);
    state.savedItems = orderItemsWithCompanions((state.saved && state.saved.items) || []);
    return state.saved;
}

// Emits each source item followed by its companion(s). A companion whose source is not in the same
// project stays where it is, as a standalone row.
function orderItemsWithCompanions (items) {
    const ids = new Set(items.map(item => item.id));
    const companionsBySource = new Map();
    items.forEach(item => {
        if (item.companionSourceId && ids.has(item.companionSourceId)) {
            const list = companionsBySource.get(item.companionSourceId) || [];
            list.push(item);
            companionsBySource.set(item.companionSourceId, list);
        }
    });
    const ordered = [];
    items.forEach(item => {
        if (item.companionSourceId && ids.has(item.companionSourceId)) {
            return; // emitted right after its source below
        }
        ordered.push(item);
        (companionsBySource.get(item.id) || []).forEach(companion => ordered.push(companion));
    });
    return ordered;
}

// Group each source item with its auto-added companion(s) so the pair renders as one outlined
// unit. Companions whose source is not in the cart render as standalone rows.
function buildCartGroups () {
    const items = state.cart.items || [];
    const ids = new Set(items.map(item => item.id));
    const companionsBySource = new Map();
    items.forEach(item => {
        if (item.companionSourceId && ids.has(item.companionSourceId)) {
            const list = companionsBySource.get(item.companionSourceId) || [];
            list.push(item);
            companionsBySource.set(item.companionSourceId, list);
        }
    });
    const groups = [];
    items.forEach(item => {
        if (item.companionSourceId && ids.has(item.companionSourceId)) {
            return; // rendered within its source's group
        }
        const companions = companionsBySource.get(item.id) || [];
        groups.push({ isCompanionGroup: Boolean(companions.length), items: [item, ...companions] });
    });
    return groups;
}

////////////////////////
// Criteo Cart Array
////////////////////////
function setCriteoCartArray () {
    window.spdc.vendor.criteoCartArray = []

    state.cart.items.forEach((item) => {
        let itemObject = {
            id: item.productIdWithPrefix,
            price: item.price,
            quantity: item.quantity
        }
        window.spdc.vendor.criteoCartArray.push(itemObject);
    });
}

////////////////////////
// GA4 View Cart Event
////////////////////////
function pushViewCartEvent () {
    gtag("event", "view_cart", buildGA4Item(state.cart.items));
}

////////////////////////
// GA4 Begin Checkout Event
////////////////////////
function pushBeginCheckoutEvent () {
    gtag("event", "begin_checkout", buildGA4Item(state.cart.items));
}

////////////////////////
// GA4 Remove From Cart Event
////////////////////////
function pushRemoveFromCartEvent (item) {
    gtag("event", "remove_from_cart", buildGA4Item(item));
}

////////////////////////
// GA4 Save For Later Event
////////////////////////
function pushSaveForLaterEvent (item) {
    gtag("event", "save_for_later", buildGA4Item(item));
}

////////////////////////
// GA4 Move To Cart Event
////////////////////////
function pushMoveToCartEvent (item) {
    gtag("event", "move_to_cart", buildGA4Item(item));
}

////////////////////////
// GA4 Utils
////////////////////////
function buildGA4Item (itemOrItems) {
    const items = [...(itemOrItems.length ? itemOrItems : [itemOrItems])];
    const project = getProjectFromOrderItem(items[0]);

    return {
        currency: "USD",
        value: state.cart.subtotalPrice,
        items: items.map(item => ({
            item_id: item.productIdWithPrefix,
            item_name: item.productName,
            currency: "USD",
            index: project.items.findIndex(el => el.id === item.id) + 1,
            price: item.price,
            quantity: item.quantity
        }))
    }
}

function moveLoyaltyRewards(target) {
    const movement = document.querySelector('.loyaltyRewardsCartPoints');
    movement && target.prepend(movement);
}

////////////////////////
// Render Handlers
////////////////////////
function renderCart () {
    state.cartGroups = buildCartGroups();

    //move loyalty rewards to safe div
    moveLoyaltyRewards(document.querySelector('.loyaltyRewardsHidden'))

    cartContainer.innerHTML = state.cart.items.length ? cartHtml(state) : cartEmptyHtml(state);
    // TODO:------------------------------------------------------------------------------------------------^^^^^ don't need to pass state once saved is own thing

    state.cart.items.forEach((item) => {
        const itemContainer = document.getElementById(`cart-item-${item.id}`);
        new CartRowController(item, itemContainer, state.loggedIn);
    });

    (state.savedItems || []).forEach(item => {
        const itemContainer = document.getElementById(`saved-item-${item.id}`);
        new SavedRowController(item, itemContainer, state.loggedIn, savedForLaterRowHtml);
    });

    renderFileUploadWarnings();

    setCriteoCartArray();

    //move loyalty rewards back
    moveLoyaltyRewards(document.querySelector('.project-ft-totals'))

    if (!state.loggedIn) {
        return;
    }
}

function renderFileUploadWarnings () {
    document.getElementById('fileUploadWarnings').innerHTML = fileUploadWarningsHtml(state.cart);
}